import { ChangeEvent, useMemo, useState } from 'react';

import { CountryCode } from 'libphonenumber-js/max';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { Group, Input, Select, TextInput } from '@mantine/core';

import {
  countryOptions,
  DEFAULT_COUNTRY,
  matchesCallingCode,
  splitE164,
  toE164
} from '@/utils/phoneNumber';

interface PhoneFieldProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}

const selectData = countryOptions.map((opt) => ({ value: opt.value, label: opt.short }));

const PhoneField = ({ value, onChange, required, error }: PhoneFieldProps) => {
  const [countryOverride, setCountryOverride] = useState<CountryCode | null>(null);

  const activeOverride =
    countryOverride !== null && matchesCallingCode(value, countryOverride) ? countryOverride : null;

  const { country: derivedCountry, national } = useMemo(
    () => splitE164(value, activeOverride ?? DEFAULT_COUNTRY),
    [value, activeOverride]
  );

  const country = activeOverride ?? derivedCountry ?? DEFAULT_COUNTRY;

  const handleCountryChange = (next: string | null) => {
    if (!next) return;
    const nextCode = next as CountryCode;
    setCountryOverride(nextCode);
    onChange(toE164(nextCode, national));
  };

  const handleNationalChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(toE164(country, event.target.value.replace(/\D/g, '')));

  return (
    <Group gap="xs" wrap="nowrap">
      <Select
        data={selectData}
        value={country}
        onChange={handleCountryChange}
        searchable
        w={140}
        styles={{ input: { paddingRight: 24 } }}
        aria-label="Country code"
      />
      <TextInput
        value={national}
        onChange={handleNationalChange}
        inputMode="tel"
        autoComplete="tel-national"
        placeholder="Phone number"
        style={{ flex: 1 }}
        error={required && !!error ? ' ' : undefined}
      />
    </Group>
  );
};

interface PhoneNumberInputProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label?: string;
  required?: boolean;
  error?: string;
}

const PhoneNumberInput = <T extends FieldValues = FieldValues>({
  control,
  name,
  label,
  required,
  error
}: PhoneNumberInputProps<T>) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <Input.Wrapper label={label ?? 'Phone Number'} required={required} error={error} my="md">
        <PhoneField value={field.value ?? ''} onChange={field.onChange} required={required} error={error} />
      </Input.Wrapper>
    )}
  />
);

export default PhoneNumberInput;
